import React from 'react'

import { cn } from './cn'
import { Menu } from './Menu'

// DOM mirrors Semantic UI React Sidebar (modules/Sidebar/*.js) for the props the app uses.
// Styles are copied from Semantic UI 2.4.2 sidebar.css for selectors that match live DOM.

const ANIMATION_DURATION = 500

export type SidebarAnimation =
  | 'overlay'
  | 'push'
  | 'scale down'
  | 'uncover'
  | 'slide out'
  | 'slide along'

export type SidebarDirection = 'top' | 'right' | 'bottom' | 'left'

export type SidebarProps = Omit<React.HTMLAttributes<HTMLElement>, 'as'> & {
  as?: React.ElementType
  animation?: SidebarAnimation
  direction?: SidebarDirection
  visible?: boolean
  onHide?: (event: MouseEvent | React.SyntheticEvent) => void
  onHidden?: () => void
  onShow?: () => void
  onVisible?: () => void
  inverted?: boolean
  vertical?: boolean
  children?: React.ReactNode
}

export type SidebarPushableProps = React.HTMLAttributes<HTMLDivElement> & {
  children?: React.ReactNode
}

export type SidebarPusherProps = React.HTMLAttributes<HTMLDivElement> & {
  dimmed?: boolean
  children?: React.ReactNode
}

export const SIDEBAR_STYLES = [
  // `.ui.sidebar`
  '[&.ui.sidebar]:fixed [&.ui.sidebar]:top-0 [&.ui.sidebar]:left-0',
  '[&.ui.sidebar]:[backface-visibility:hidden] [&.ui.sidebar]:transition-none',
  '[&.ui.sidebar]:will-change-transform [&.ui.sidebar]:[transform:translate3d(0,0,0)]',
  '[&.ui.sidebar]:invisible [&.ui.sidebar]:max-h-full [&.ui.sidebar]:z-[102]',
  '[&.ui.sidebar]:!h-full [&.ui.sidebar]:!rounded-none [&.ui.sidebar]:!m-0',
  '[&.ui.sidebar]:!overflow-y-auto',
  '[&.ui.sidebar>*]:[backface-visibility:hidden]',
  // `.ui.left.sidebar`
  '[&.ui.left.sidebar]:right-auto [&.ui.left.sidebar]:left-0',
  '[&.ui.left.sidebar:not(:where(.visible))]:[transform:translate3d(-100%,0,0)] [&.ui.left.sidebar]:w-[260px]',
  // `.ui.push.sidebar`
  '[&.ui.push.sidebar]:[transition:transform_500ms,-webkit-transform_500ms] [&.ui.push.sidebar]:z-[102]',
  // Ties with `.ui.visible.sidebar` / `.ui.visible.push.sidebar`, which come later and win.
  '[&.ui.left.push.sidebar:not(:where(.visible))]:[transform:translate3d(-100%,0,0)]',
  // `.ui.visible.sidebar` / left visible / push visible
  '[&.ui.visible.sidebar]:visible [&.ui.visible.sidebar]:[transform:translate3d(0,0,0)]',
  '[&.ui.left.visible.sidebar]:shadow-[0_0_20px_rgba(34,36,38,0.15)]',
  '[&.ui.visible.push.sidebar]:[transform:translate3d(0%,0,0)]',
  // `.ui.animating.sidebar`
  '[&.ui.animating.sidebar]:visible',
  // `.ui.visible.left.sidebar ~ .fixed`
  '[&.ui.visible.left.sidebar~.fixed]:[transform:translate3d(260px,0,0)]',
].join(' ')

const PUSHABLE_STYLES = [
  '[&.pushable]:h-full [&.pushable]:overflow-x-hidden [&.pushable]:!p-0',
  '[&.pushable:not(body)]:[transform:translate3d(0,0,0)]',
  '[&.pushable:not(body)>.pusher]:after:absolute',
  '[&.pushable>.pusher]:relative [&.pushable>.pusher]:[backface-visibility:hidden]',
  '[&.pushable>.pusher]:overflow-hidden [&.pushable>.pusher]:min-h-full',
  '[&.pushable>.pusher]:[transition:transform_500ms,-webkit-transform_500ms] [&.pushable>.pusher]:z-[2]',
  '[&.pushable>.pusher]:bg-inherit',
  '[&.pushable>.pusher]:after:fixed [&.pushable>.pusher]:after:top-0 [&.pushable>.pusher]:after:right-0',
  '[&.pushable>.pusher]:after:content-[""] [&.pushable>.pusher]:after:bg-[rgba(0,0,0,0.4)]',
  '[&.pushable>.pusher]:after:overflow-hidden [&.pushable>.pusher]:after:opacity-0',
  '[&.pushable>.pusher]:after:[transition:opacity_500ms] [&.pushable>.pusher]:after:will-change-[opacity]',
  '[&.pushable>.pusher]:after:z-[1000]',
  '[&.pushable>.pusher.dimmed]:after:!w-full [&.pushable>.pusher.dimmed]:after:!h-full',
  '[&.pushable>.pusher.dimmed]:after:!opacity-100',
].join(' ')

function sidebarMarkerWords(props: {
  animation?: SidebarAnimation
  direction?: SidebarDirection
  animating?: boolean
  visible?: boolean
}): string {
  return cn(
    'ui',
    props.animation,
    props.direction,
    props.animating && 'animating',
    props.visible && 'visible',
    'sidebar',
  )
}

const SidebarRoot = React.forwardRef<HTMLElement, SidebarProps>(
  function Sidebar(
    {
      as: ElementType = 'div',
      animation = 'push',
      direction = 'left',
      visible = false,
      onHide,
      onHidden,
      onShow,
      onVisible,
      className,
      children,
      inverted,
      vertical,
      ...rest
    }: SidebarProps,
    ref,
  ) {
    const innerRef = React.useRef<HTMLElement | null>(null)
    const mergedRef = (node: HTMLElement | null) => {
      innerRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    }

    const [animationTick, setAnimationTick] = React.useState(0)
    const prevVisible = React.useRef(visible)
    const skipNextCallback = React.useRef(false)
    const animationTimer = React.useRef<
      ReturnType<typeof setTimeout> | undefined
    >(undefined)

    React.useEffect(() => {
      if (!!visible !== !!prevVisible.current) {
        setAnimationTick(t => t + 1)
      }
      prevVisible.current = visible
    }, [visible])

    React.useEffect(() => {
      if (animationTick === 0) return
      clearTimeout(animationTimer.current)
      const opening = visible
      if (!skipNextCallback.current) {
        if (opening) onVisible?.()
        else onHide?.({} as React.SyntheticEvent)
      } else {
        skipNextCallback.current = false
      }
      animationTimer.current = setTimeout(() => {
        setAnimationTick(0)
        if (opening) onShow?.()
        else onHidden?.()
      }, ANIMATION_DURATION)
      return () => clearTimeout(animationTimer.current)
    }, [animationTick, onHidden, onHide, onShow, onVisible, visible])

    const onHideRef = React.useRef(onHide)
    onHideRef.current = onHide

    React.useEffect(() => {
      if (!visible) return
      // In the browser React 17 can run this effect while the click that opened the
      // sidebar is still bubbling, so the new listener would see that click and close it.
      const openingEvent = window.event
      const handleDocumentClick = (e: MouseEvent) => {
        if (openingEvent && e === openingEvent) return
        if (innerRef.current?.contains(e.target as Node)) return
        skipNextCallback.current = true
        onHideRef.current?.(e)
      }
      document.addEventListener('click', handleDocumentClick)
      return () => document.removeEventListener('click', handleDocumentClick)
    }, [visible])

    const sidebarClass = cn(
      sidebarMarkerWords({
        animation,
        direction,
        animating: animationTick > 0,
        visible,
      }),
      SIDEBAR_STYLES,
      className,
    )

    const menuPassthrough =
      ElementType === Menu ? { inverted, vertical, ...rest } : rest

    if (ElementType === Menu) {
      return (
        <Menu ref={mergedRef} className={sidebarClass} {...menuPassthrough}>
          {children}
        </Menu>
      )
    }

    return (
      <ElementType
        ref={mergedRef}
        className={sidebarClass}
        {...menuPassthrough}
      >
        {children}
      </ElementType>
    )
  },
)

function SidebarPushable({
  className,
  children,
  ...rest
}: SidebarPushableProps) {
  return (
    <div className={cn('pushable', PUSHABLE_STYLES, className)} {...rest}>
      {children}
    </div>
  )
}

function SidebarPusher({
  dimmed,
  className,
  children,
  ...rest
}: SidebarPusherProps) {
  return (
    <div className={cn('pusher', dimmed && 'dimmed', className)} {...rest}>
      {children}
    </div>
  )
}

export const Sidebar = Object.assign(SidebarRoot, {
  Pushable: SidebarPushable,
  Pusher: SidebarPusher,
})

export default Sidebar
